// Ortak dosya seçici — Geçmiş Rapor Aktarımı (Task 12, Eng review 6A + Tasarım review 16A).
// AttachmentUploader'ın ("components/form-fill/AttachmentUploader.tsx") drop/pick deseninin
// genellenmiş hali; AttachmentUploader'a DOKUNULMAZ (TODO-UX-07) — o kendi upload/scan
// akışını sürdürür, bu bileşen yalnız dosya SEÇİMİ + istemci-taraflı red kontrolü yapar.
//
// 16A: görünür "Dosya seç" düğmesi + gizli input + sürükle-bırak alanı; klavye (Enter/Space)
// dosya seçiciyi açar; disabled'da hem sürükleme hem seçim kapanır (aria-disabled).
// Reddedilen dosyalar `--color-error` ile ayrı listede gösterilir (ad + sebep).

import { useId, useRef, useState } from "react";

export interface FileDropzoneProps {
  /** `<input accept>` — ör. "application/pdf,.pdf,.docx". */
  accept: string;
  multiple?: boolean;
  maxBytes: number;
  /** Kabul edilen (reddedilmeyen) dosyalar — çağıran taraf ekler/gruplar. */
  onFiles: (files: File[]) => void;
  /** Dosya bazlı red kararı; null = kabul. maxBytes aşımı ayrıca burada da kontrol edilir. */
  rejectReason: (file: File) => string | null;
  hint: string;
  /** Görünür "Dosya seç" düğmesinin metni. */
  label: string;
  disabled?: boolean;
  /** Tek seferde eklenebilecek üst sınır — aşan dosyalar reddedilenler listesine düşer. */
  maxFiles?: number;
}

interface RejectedEntry {
  name: string;
  reason: string;
}

export function FileDropzone(props: FileDropzoneProps): JSX.Element {
  const {
    accept,
    multiple = true,
    maxBytes,
    onFiles,
    rejectReason,
    hint,
    label,
    disabled = false,
    maxFiles,
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejected, setRejected] = useState<RejectedEntry[]>([]);
  const hintId = useId();

  function handleFiles(list: FileList | null): void {
    if (!list || disabled) return;
    const incoming = Array.from(list);
    const accepted: File[] = [];
    const nextRejected: RejectedEntry[] = [];
    for (const file of incoming) {
      if (maxFiles !== undefined && accepted.length >= maxFiles) {
        nextRejected.push({
          name: file.name,
          reason: `En fazla ${maxFiles.toString()} dosya eklenebilir`,
        });
        continue;
      }
      const reason =
        rejectReason(file) ?? (file.size > maxBytes ? "Dosya boyut sınırını aşıyor" : null);
      if (reason) {
        nextRejected.push({ name: file.name, reason });
        continue;
      }
      accepted.push(file);
    }
    setRejected(nextRejected);
    if (accepted.length > 0) onFiles(accepted);
  }

  function openPicker(): void {
    if (!disabled) inputRef.current?.click();
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  }

  return (
    <div>
      {/* Sürükleme alanı `role="button"` DEĞİL: içinde görünür "Dosya seç" düğmesi
          var, iç içe etkileşimli öğe axe `nested-interactive` ihlali üretirdi (15A —
          /projects/import axe critical 0). Klavye erişimi düğme üzerinden sağlanır;
          sürükle-bırak fare için ek bir kolaylıktır. */}
      <div
        aria-disabled={disabled || undefined}
        onDragOver={(e) => {
          if (!disabled) e.preventDefault();
        }}
        onDrop={onDrop}
        className="rounded-lg p-4 text-center"
        style={{
          background: "var(--color-bg)",
          border: "2px dashed var(--color-border)",
          color: "var(--color-text-3)",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          className="hidden"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = ""; // aynı dosyayı tekrar seçmeye izin ver
          }}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={openPicker}
          aria-describedby={hintId}
          className="rounded-lg px-3 py-1.5 text-sm"
          style={{
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text)",
            fontWeight: 500,
          }}
        >
          {label}
        </button>
        <div id={hintId} style={{ color: "var(--color-text-2)", fontSize: "0.8rem", marginTop: 8 }}>
          {hint}
        </div>
      </div>

      {rejected.length > 0 && (
        // 15A — 14px altı metin --color-text-2; --color-error yalnız ikonda (bar/ikon istisnası).
        <ul className="mt-2 flex flex-col gap-1" style={{ fontSize: "0.78rem" }}>
          {rejected.map((r, i) => (
            <li key={`${r.name}-${i.toString()}`} style={{ color: "var(--color-text-2)" }}>
              <span aria-hidden="true" style={{ color: "var(--color-error)" }}>
                ✕{" "}
              </span>
              {r.name} — {r.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
